/** @module @liquidefy/code-editor/syntax/syntax-language @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  LanguageId,
  LanguageDefinition,
  LanguageConfiguration,
  BracketPair,
  FoldingRules,
  IndentationRules,
  CommentRule,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asLanguageId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — registry state
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * The language registry — all registered language definitions indexed by ID.
 * Also maintains extension/filename/firstLine lookup tables for fast detection.
 */
export interface LanguageRegistryState {
  readonly languages: Map<LanguageId, LanguageDefinition>;
  readonly extensionMap: Map<string, LanguageId>;
  readonly filenameMap: Map<string, LanguageId>;
  readonly firstLineMap: Array<{ readonly pattern: RegExp; readonly languageId: LanguageId }>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 REGISTRY MANAGEMENT — register, query
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an empty language registry with no registered languages.
 */
export function createLanguageRegistry(): LanguageRegistryState {
  return {
    languages: new Map(),
    extensionMap: new Map(),
    filenameMap: new Map(),
    firstLineMap: [],
  };
}

/**
 * Register a language definition in the registry.
 * Builds extension, filename, and first-line lookup tables automatically.
 * Existing registrations for the same language ID are replaced.
 *
 * @param state      Current registry state.
 * @param definition The language definition to register.
 */
export function registerLanguage(
  state: LanguageRegistryState,
  definition: LanguageDefinition,
): EditorResult<LanguageRegistryState> {
  if (!definition.id) {
    return err('INVALID_LANGUAGE', 'Language definition must have an id');
  }
  if (!definition.name) {
    return err('INVALID_LANGUAGE', 'Language definition must have a name');
  }

  const languages = new Map(state.languages);
  languages.set(definition.id, definition);

  const extensionMap = new Map(state.extensionMap);
  for (const ext of definition.extensions) {
    // Normalize: ensure leading dot
    const key = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
    extensionMap.set(key, definition.id);
  }

  const filenameMap = new Map(state.filenameMap);
  for (const filename of definition.filenames) {
    filenameMap.set(filename.toLowerCase(), definition.id);
  }

  let firstLineMap = state.firstLineMap.filter(e => e.languageId !== definition.id);
  if (definition.firstLine) {
    try {
      const pattern = new RegExp(definition.firstLine);
      firstLineMap = [...firstLineMap, { pattern, languageId: definition.id }];
    } catch {
      // Invalid firstLine regex — skip
    }
  }

  const next: LanguageRegistryState = { languages, extensionMap, filenameMap, firstLineMap };
  return ok(next, 'LANGUAGE_REGISTERED', `Language registered: ${definition.name}`);
}

/**
 * Detect the likely language for a file based on its filename and (optionally) its
 * first line of content.
 *
 * Detection priority (highest to lowest):
 * 1. Exact filename match (e.g. `Makefile`, `Dockerfile`).
 * 2. File extension match (e.g. `.ts`, `.py`).
 * 3. First-line shebang or content match (e.g. `#!/usr/bin/env python3`).
 * 4. Falls back to `'plaintext'`.
 *
 * @param state    Current registry state.
 * @param filename The full filename or path (basename used for matching).
 * @param content  Optional first line of the file for shebang/content detection.
 */
export function detectLanguage(
  state: LanguageRegistryState,
  filename: string,
  content?: string,
): LanguageId {
  const basename = filename.includes('/') ? filename.split('/').pop() ?? filename : filename;
  const baseLower = basename.toLowerCase();

  // 1. Exact filename
  const byFilename = state.filenameMap.get(baseLower);
  if (byFilename) return byFilename;

  // 2. Extension
  const dotIdx = baseLower.lastIndexOf('.');
  if (dotIdx !== -1) {
    const ext = baseLower.slice(dotIdx);
    const byExt = state.extensionMap.get(ext);
    if (byExt) return byExt;
  }

  // 3. First-line match
  if (content) {
    const firstLine = content.split('\n')[0] ?? '';
    for (const { pattern, languageId } of state.firstLineMap) {
      if (pattern.test(firstLine)) return languageId;
    }
  }

  return asLanguageId('plaintext');
}

/**
 * Retrieve the full LanguageConfiguration for a registered language.
 * Returns an error if the language is not registered.
 *
 * @param state      Current registry state.
 * @param languageId The language to look up.
 */
export function getLanguageConfig(
  state: LanguageRegistryState,
  languageId: LanguageId,
): EditorResult<LanguageConfiguration> {
  const def = state.languages.get(languageId);
  if (!def) {
    return err('LANGUAGE_NOT_FOUND', `Language not registered: ${languageId}`);
  }
  return ok(def.configuration, 'LANGUAGE_CONFIG_FOUND', `Config for: ${def.name}`);
}

/**
 * Check whether a language is registered in the registry.
 *
 * @param state      Current registry state.
 * @param languageId The language to check.
 */
export function isLanguageRegistered(
  state: LanguageRegistryState,
  languageId: LanguageId,
): boolean {
  return state.languages.has(languageId);
}

/**
 * List all registered languages, sorted alphabetically by name.
 *
 * @param state Current registry state.
 */
export function listLanguages(state: LanguageRegistryState): LanguageDefinition[] {
  return [...state.languages.values()].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/**
 * Detect and resolve the language for a given file path, returning the full
 * LanguageDefinition or an error if no language was found.
 *
 * @param state    Current registry state.
 * @param filePath Full file path or filename.
 * @param content  Optional file content for first-line detection.
 */
export function getLanguageForFile(
  state: LanguageRegistryState,
  filePath: string,
  content?: string,
): EditorResult<LanguageDefinition> {
  const languageId = detectLanguage(state, filePath, content);
  const def = state.languages.get(languageId);
  if (!def) {
    // plaintext is the fallback — try to return a synthetic definition
    return err('LANGUAGE_NOT_FOUND', `No language definition for: ${languageId}`);
  }
  return ok(def, 'LANGUAGE_FOUND', `Detected language: ${def.name}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 CONFIGURATION ACCESSORS — brackets, folding, indentation, comments
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get the open/close bracket pairs defined for this language configuration.
 * Used by bracket matching, rainbow brackets, and auto-close logic.
 *
 * @param config The LanguageConfiguration to read.
 */
export function getBracketPairs(config: LanguageConfiguration): BracketPair[] {
  return [...config.brackets];
}

/**
 * Get folding rules from the language configuration.
 * Includes both indent-based folding (offSide) and marker-based folding
 * (start/end regex patterns).
 *
 * @param config The LanguageConfiguration to read.
 */
export function getFoldingRules(config: LanguageConfiguration): FoldingRules {
  return config.folding;
}

/**
 * Get the auto-indentation rules from the language configuration.
 * These are used by the editor to determine the correct indentation level
 * when the user presses Enter or pastes content.
 *
 * @param config The LanguageConfiguration to read.
 */
export function getIndentationRules(config: LanguageConfiguration): IndentationRules {
  return config.indentation;
}

/**
 * Get the comment delimiters for a language.
 * Returns both line comment prefix (e.g. `//`) and block comment delimiters
 * (e.g. `['/*', '*\/']`) as defined in the language configuration.
 *
 * @param config The LanguageConfiguration to read.
 */
export function getCommentTokens(config: LanguageConfiguration): CommentRule {
  return config.comments;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 BUILT-IN LANGUAGE DEFINITIONS — minimal plaintext and common languages
// ═══════════════════════════════════════════════════════════════════════════════

/** Default empty language configuration used as a base or fallback. */
export const EMPTY_LANGUAGE_CONFIG: LanguageConfiguration = {
  comments: {},
  brackets: [],
  autoClosingPairs: [],
  surroundingPairs: [],
  folding: {},
  indentation: {
    increaseIndentPattern: '',
    decreaseIndentPattern: '',
  },
};

/**
 * The built-in `plaintext` language definition.
 * Registered in every registry by default via `createRegistryWithBuiltins()`.
 */
export const PLAINTEXT_LANGUAGE: LanguageDefinition = {
  id: asLanguageId('plaintext'),
  name: 'Plain Text',
  aliases: ['text', 'txt'],
  extensions: ['.txt'],
  filenames: [],
  mimeTypes: ['text/plain'],
  configuration: EMPTY_LANGUAGE_CONFIG,
};

/**
 * Create a language registry pre-loaded with the plaintext built-in.
 * Most consumers should call this rather than `createLanguageRegistry()`.
 */
export function createRegistryWithBuiltins(): LanguageRegistryState {
  const base = createLanguageRegistry();
  const result = registerLanguage(base, PLAINTEXT_LANGUAGE);
  return result.success ? result.value : base;
}

/**
 * Remove a language from the registry.
 * Its extension/filename/firstLine entries are also cleaned up.
 *
 * @param state      Current registry state.
 * @param languageId The language to remove.
 */
export function unregisterLanguage(
  state: LanguageRegistryState,
  languageId: LanguageId,
): EditorResult<LanguageRegistryState> {
  if (!state.languages.has(languageId)) {
    return err('LANGUAGE_NOT_FOUND', `Language not registered: ${languageId}`);
  }

  const def = state.languages.get(languageId)!;
  const languages = new Map(state.languages);
  languages.delete(languageId);

  const extensionMap = new Map(state.extensionMap);
  for (const ext of def.extensions) {
    const key = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
    if (extensionMap.get(key) === languageId) extensionMap.delete(key);
  }

  const filenameMap = new Map(state.filenameMap);
  for (const filename of def.filenames) {
    if (filenameMap.get(filename.toLowerCase()) === languageId) {
      filenameMap.delete(filename.toLowerCase());
    }
  }

  const firstLineMap = state.firstLineMap.filter(e => e.languageId !== languageId);

  return ok(
    { languages, extensionMap, filenameMap, firstLineMap },
    'LANGUAGE_UNREGISTERED',
    `Language unregistered: ${def.name}`,
  );
}
